import React from 'react';
import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Paper,
  Typography,
  Stack,
  Box,
  Chip,
  Avatar,
  Switch,
  FormControlLabel,
  IconButton,
  FormControl,
  InputLabel,
  MenuItem
} from '@mui/material';
import Select from '../SearchableSelect';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TimePicker24Field from "../TimePicker24Field";

function pad(value) {
  return String(value).padStart(2, '0');
}

function parseTimePickerValue(value) {
  if (!value || typeof value !== 'string') return null;
  const [hours, minutes] = value.split(':').map(Number);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
}

function formatTimePickerValue(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export default function PackageFormDialog({
  open,
  onClose,
  packageForm,
  onFieldChange,
  products,
  onToggleProduct,
  onUpdateProductField,
  onUpdateCourse,
  selectedProducts,
  selectedProductsTotal,
  computedPackagePrice,
  onSave,
  onUploadImages,
  onRemoveImage,
  uploadingImages,
  gt,
  formatMoney,
  theme,
}) {
  const fileInputRef = React.useRef(null);
  const initializedDialogRef = React.useRef('');
  const [activeCourseIndex, setActiveCourseIndex] = React.useState(1);
  const [courseNames, setCourseNames] = React.useState({});
  const isTastingMenu = packageForm.type === 'TASTING_MENU';

  React.useEffect(() => {
    if (!open) {
      initializedDialogRef.current = '';
      return;
    }

    const dialogKey = `${packageForm.id || 'new'}-${packageForm.type || 'STANDARD'}`;
    if (initializedDialogRef.current === dialogKey) return;
    initializedDialogRef.current = dialogKey;

    const names = {};
    (packageForm.items || []).forEach((item) => {
      const courseIndex = Math.max(1, Number(item.courseIndex) || 1);
      if (!names[courseIndex] && item.courseName) {
        names[courseIndex] = item.courseName;
      }
    });
    const firstCourseIndex = Math.min(
      ...(packageForm.items || []).map((item) => Math.max(1, Number(item.courseIndex) || 1)),
      1
    );
    setCourseNames(names);
    setActiveCourseIndex(firstCourseIndex);
  }, [open, packageForm.id, packageForm.items, packageForm.type]);

  const existingCourseIndexes = React.useMemo(() => {
    const indexes = new Set(
      (packageForm.items || []).map((item) => Math.max(1, Number(item.courseIndex) || 1))
    );
    indexes.add(activeCourseIndex);
    return [...indexes].sort((a, b) => a - b);
  }, [activeCourseIndex, packageForm.items]);

  const handleCourseNameChange = (value) => {
    setCourseNames((prev) => ({ ...prev, [activeCourseIndex]: value }));
    onUpdateCourse?.(activeCourseIndex, 'courseName', value);
  };

  const handleNextCourse = () => {
    setActiveCourseIndex(activeCourseIndex + 1);
  };

  const handlePickImages = () => {
    fileInputRef.current?.click();
  };

  const handleFilesChange = async (e) => {
    const files = e.target.files;
    await onUploadImages?.(files);
    e.target.value = '';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 800 }}>
        {packageForm.id ? gt('allestimenti.editPackage') : gt('allestimenti.newPackageModal')}
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2.2}>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              label={gt('allestimenti.packageName')}
              value={packageForm.name}
              onChange={(e) => onFieldChange('name', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="package-type-label">{gt('allestimenti.packageType')}</InputLabel>
              <Select
                labelId="package-type-label"
                label={gt('allestimenti.packageType')}
                value={packageForm.type || 'STANDARD'}
                onChange={(e) => onFieldChange('type', e.target.value)}
              >
                <MenuItem value="STANDARD">{gt('allestimenti.packageTypeStandard')}</MenuItem>
                <MenuItem value="TASTING_MENU">{gt('allestimenti.packageTypeTastingMenu')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label={gt('allestimenti.manualPriceOptional')}
              type="number"
              value={packageForm.manualPrice}
              onChange={(e) => onFieldChange('manualPrice', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label={gt('allestimenti.capacity')}
              type="number"
              value={packageForm.capacity}
              onChange={(e) => onFieldChange('capacity', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={9}>
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', pl: 1 }}>
              <FormControlLabel
                control={<Switch checked={packageForm.active} onChange={(e) => onFieldChange('active', e.target.checked)} />}
                label={gt('allestimenti.packageActive')}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <TimePicker24Field
              label={gt('allestimenti.activeFrom')}
              value={parseTimePickerValue(packageForm.activeFrom || '00:00')}
              onChange={(value) => onFieldChange('activeFrom', formatTimePickerValue(value))}
              ampm={false}
              format="HH:mm"
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  inputProps={{
                    ...params.inputProps,
                    readOnly: true,
                    autoComplete: 'off',
                    'aria-autocomplete': 'none',
                  }}
                  InputProps={{
                    ...params.InputProps,
                    autoComplete: 'off',
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TimePicker24Field
              label={gt('allestimenti.activeTo')}
              value={parseTimePickerValue(packageForm.activeTo || '23:59')}
              onChange={(value) => onFieldChange('activeTo', formatTimePickerValue(value))}
              ampm={false}
              format="HH:mm"
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  inputProps={{
                    ...params.inputProps,
                    readOnly: true,
                    autoComplete: 'off',
                    'aria-autocomplete': 'none',
                  }}
                  InputProps={{
                    ...params.InputProps,
                    autoComplete: 'off',
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 1.2 }}>
              <Button variant="outlined" onClick={handlePickImages} disabled={uploadingImages}>
                {uploadingImages ? gt('allestimenti.uploadingImages') : gt('allestimenti.uploadImages')}
              </Button>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              {gt('allestimenti.imageFormats')}
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                onChange={handleFilesChange}
                style={{ display: 'none' }}
              />
            </Stack>

            {(packageForm.imagePaths || []).length > 0 && (
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.4 }}>
                {(packageForm.imagePaths || []).map((imgPath) => (
                  <Box
                    key={imgPath}
                    sx={{
                      width: 74,
                      height: 74,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      overflow: 'hidden',
                      position: 'relative',
                      backgroundColor: theme.palette.background.default,
                    }}
                  >
                    <img
                      src={imgPath}
                      alt="package"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => onRemoveImage?.(imgPath)}
                      sx={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        backgroundColor: 'rgba(0,0,0,0.45)',
                        color: '#fff',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.6)' },
                      }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            )}

            <TextField
              fullWidth
              multiline
              minRows={3}
              label={gt('allestimenti.description')}
              value={packageForm.description}
              onChange={(e) => onFieldChange('description', e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.2 }}>
              {gt('allestimenti.selectIncludedProducts')}
            </Typography>
            {isTastingMenu && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: 3,
                  border: `1px solid ${theme.palette.primary.main}`,
                  backgroundColor: theme.palette.background.default,
                }}
              >
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: 800, mr: 0.5 }}>
                      {gt('allestimenti.currentCourse')}:
                    </Typography>
                    {existingCourseIndexes.map((courseIndex) => (
                      <Chip
                        key={courseIndex}
                        clickable
                        color={courseIndex === activeCourseIndex ? 'primary' : 'default'}
                        variant={courseIndex === activeCourseIndex ? 'filled' : 'outlined'}
                        label={`${gt('allestimenti.courseIndex')} ${courseIndex}`}
                        onClick={() => setActiveCourseIndex(courseIndex)}
                      />
                    ))}
                  </Stack>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.2}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      label={gt('allestimenti.courseNameOptional')}
                      value={courseNames[activeCourseIndex] || ''}
                      onChange={(e) => handleCourseNameChange(e.target.value)}
                    />
                    <Button
                      variant="contained"
                      endIcon={<ArrowForwardIcon />}
                      onClick={handleNextCourse}
                      sx={{
                        minWidth: { sm: 132 },
                        height: 40,
                        px: 2.5,
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                        textTransform: 'none',
                        fontWeight: 800,
                      }}
                    >
                      {gt('allestimenti.nextCourse')}
                    </Button>
                  </Stack>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    {gt('allestimenti.courseFlowHint')}
                  </Typography>
                </Stack>
              </Paper>
            )}
            <Grid container spacing={1.5}>
              {products.map((product) => {
                const currentItem = (packageForm.items || []).find((item) =>
                  String(item.productId) === String(product.id) &&
                  (!isTastingMenu || Number(item.courseIndex || 1) === activeCourseIndex)
                );
                const selected = Boolean(currentItem);
                const currentQty = currentItem?.quantity || 1;

                return (
                  <Grid item xs={12} md={6} key={product.id}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        border: selected ? `1px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.default,
                      }}
                    >
                      <Stack spacing={1.2}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                          <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0 }}>
                            <Avatar src={product.photoPath || ''} alt={product.name} variant="rounded" sx={{ width: 42, height: 42 }}>
                              {product.name?.[0]?.toUpperCase() || '?'}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 700 }} noWrap>{product.name}</Typography>
                              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }} noWrap>{product.categoryName}</Typography>
                            </Box>
                          </Stack>
                          <Chip label={formatMoney(product.price)} sx={{ fontWeight: 700 }} />
                        </Stack>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                          <Button
                            size="small"
                            variant={selected ? 'contained' : 'outlined'}
                            onClick={() => onToggleProduct(
                              product.id,
                              activeCourseIndex,
                              courseNames[activeCourseIndex] || ''
                            )}
                          >
                            {selected ? gt('allestimenti.remove') : gt('allestimenti.add')}
                          </Button>
                          {selected && (
                            <>
                              <TextField
                                size="small"
                                type="number"
                                label={gt('allestimenti.quantityShort')}
                                value={currentQty}
                                inputProps={{ min: 1 }}
                                onChange={(e) => onUpdateProductField(currentItem.clientKey, 'quantity', e.target.value)}
                                sx={{ width: { xs: '100%', sm: 100 } }}
                              />
                            </>
                          )}
                        </Stack>
                      </Stack>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Paper elevation={0} sx={{ mt: 1, p: 2, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                {gt('allestimenti.selectionSummary')}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {gt('allestimenti.selectedProducts')}: <strong>{selectedProducts.length}</strong>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {gt('allestimenti.productsTotal')}: <strong>{formatMoney(selectedProductsTotal)}</strong>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                {gt('allestimenti.finalPackagePrice')}: <strong>{formatMoney(computedPackagePrice)}</strong>
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700 }}>
          {gt('allestimenti.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={!packageForm.name.trim() || (packageForm.items || []).length === 0}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          {packageForm.id ? gt('allestimenti.saveChanges') : gt('allestimenti.createPackage')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
